import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar7 from "../components/Navbar7";
import { retrieveViews, createFlow } from "../services/database";
import { toast } from 'react-toastify';

const BuildFlow = () => {
  
  const location = useLocation();
  const navigate = useNavigate();

  const { projectId } = location.state || {};

  const [views, setViews] = useState([]);
  const [flowItems, setFlowItems] = useState([]);
  const [flowTitle, setFlowTitle] = useState("");
  const [draggingViewId, setDraggingViewId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch valid views from the DB on component mount
  useEffect(() => {
    if (!projectId) {
      console.error("Project ID is missing");
      navigate("/projects");
      return;
    }

    const fetchViews = async () => {
      try {
        const fetchedViews = await retrieveViews(projectId);

        // Filter out results so only versions with revisionStatus === "complete" or no revisionStatus
        // Then sort them by time_created (ascending) and label them with versionIndex
        const validViews = fetchedViews
          .map((view) => {
            const validVersions = (view.versions || []).filter(
              (ver) => !ver.revisionStatus || ver.revisionStatus === "complete"
            );
            if (validVersions.length === 0) {
              return null;
            }
            // Sort versions by time_created ascending
            validVersions.sort((a, b) => {
              const timeA = new Date(a.time_created || new Date());
              const timeB = new Date(b.time_created || new Date());
              return timeA - timeB; // oldest to newest
            });
            // Assign versionIndex (1-based), oldest is version 1, newest is version n
            validVersions.forEach((ver, idx) => {
              ver.versionIndex = idx + 1;
            });

            const newestVersion = validVersions[validVersions.length - 1];

            return {
              ...view,
              versions: validVersions,
              selectedVersionId: newestVersion?.versionId || ""
            };
          })
          .filter(Boolean);

        setViews(validViews);
      } catch (error) {
        console.error("Error retrieving views:", error);
      }
    };

    fetchViews();
  }, [projectId, navigate]);

  // Handle version change in the left column's <select>
  const handleVersionChange = (viewId, newVersionId) => {
    setViews((prev) =>
      prev.map((v) => {
        if (v._id === viewId) {
          return {
            ...v,
            selectedVersionId: newVersionId
          };
        }
        return v;
      })
    );
  };

  // Drag and drop event handlers
  const handleDragStart = (e, viewId) => {
    setDraggingViewId(viewId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!draggingViewId) return;

    const viewObj = views.find((v) => v._id === draggingViewId);
    if (!viewObj) return;

    // Check if already in flow
    const alreadyInFlow = flowItems.some((item) => item.viewId === draggingViewId);
    if (alreadyInFlow) {
      return; // do nothing if it is already present
    }

    const versionId =
      viewObj.selectedVersionId || viewObj.versions[viewObj.versions.length - 1].versionId;
    const selectedVersion = viewObj.versions.find(
      (ver) => ver.versionId === versionId
    );

    setFlowItems((prev) => [
      ...prev,
      {
        viewId: viewObj._id,
        title: viewObj.title,
        versionId,
        versionIndex: selectedVersion?.versionIndex || 1
      }
    ]);

    setDraggingViewId(null);
  };

  const handleAddToFlow = (view) => {
    const alreadyInFlow = flowItems.some((item) => item.viewId === view._id);
    if (alreadyInFlow) {
      return;
    }

    const versionId = view.selectedVersionId || view.versions[view.versions.length - 1].versionId;
    const selectedVersion = view.versions.find((ver) => ver.versionId === versionId);

    setFlowItems((prev) => [
      ...prev,
      {
        viewId: view._id,
        title: view.title,
        versionId,
        versionIndex: selectedVersion?.versionIndex || 1
      }
    ]);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    setFlowItems((prev) => {
      const newArray = [...prev];
      const temp = newArray[index - 1];
      newArray[index - 1] = newArray[index];
      newArray[index] = temp;
      return newArray;
    });
  };

  const handleMoveDown = (index) => {
    if (index === flowItems.length - 1) return;
    setFlowItems((prev) => {
      const newArray = [...prev];
      const temp = newArray[index + 1];
      newArray[index + 1] = newArray[index];
      newArray[index] = temp;
      return newArray;
    });
  };

  const handleRemove = (index) => {
    setFlowItems((prev) => {
      const newArray = [...prev];
      newArray.splice(index, 1);
      return newArray;
    });
  };

  const handleSaveFlow = async () => {
    if (!flowTitle.trim()) {
        toast.error('Please enter a flow name');
        return;
      }
      if (flowTitle.trim().length < 2) {
        toast.error('Flow name must be at least 2 characters long');
        return;
      }

    if (flowItems.length < 2) {
      toast.error('A flow needs at least 2 views to create a meaningful sequence. Please add more views.');
      return;
    }

    try {
      setIsLoading(true);
      const now = new Date().toISOString();
      const flowData = {
        projectId,
        flowTitle: flowTitle.trim(),
        timeCreated: now,
        numberOfViews: flowItems.length,
        order: flowItems.map((item, idx) => ({
          viewId: item.viewId,
          versionId: item.versionId,
          orderId: idx + 1
        }))
      };

      const response = await createFlow(projectId, flowData);

      if (response && response.status === "success") {
        navigate("/view-flow", {
          state: {
            projectId,
            flowId: response.flowId
          }
        });
      } else {
        alert("Failed to create flow.");
      }
    } catch (error) {
      console.error("Error creating flow:", error);
      alert("Error creating flow. Check console.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen font-sans antialiased bg-white">
      <Navbar7 backPath="/project-details" projectId={projectId} title="Build Flow"/>

      <div className="container mx-auto p-6">
        {/* Mobile view - Stacked vertically */}
        <div className="flex flex-col md:hidden space-y-6">
          {/* Views Section */}
          <div
            id="views-section"
            className="w-full border p-4 rounded shadow-sm"
          >
            <h2 className="text-xl font-semibold mb-4">Views</h2>
            <div id="views-list" className="space-y-4">
              {views.length === 0 ? (
                <p className="text-gray-600">No valid views found.</p>
              ) : (
                views.map((view) => (
                  <div
                    key={view._id}
                    className="view-card p-4 border rounded flex justify-between items-center bg-slate-100"
                    data-view-id={view._id}
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, view._id)}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{view.title}</span>
                      {/* Version dropdown */}
                      <div className="inline-block">
                        <select
                          className="version-select mt-2 text-sm text-slate-500"
                          value={view.selectedVersionId}
                          onChange={(e) => handleVersionChange(view._id, e.target.value)}
                        >
                          {view.versions
                            .slice()
                            .sort((a, b) => b.versionIndex - a.versionIndex) // sort descending: most recent first
                            .map((ver) => (
                              <option key={ver.versionId} value={ver.versionId}>
                                Version {ver.versionIndex}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    <button
                      className="move-to-flow text-slate-900 hover:text-slate-700"
                      title="Add to Flow"
                      onClick={() => handleAddToFlow(view)}
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
          
          {/* Flow Section */}
          <div
            id="flow-section"
            className="w-full border p-4 rounded shadow-sm"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <h2 className="text-xl font-semibold mb-4">Flow</h2>

            <div className="mb-4">
              <input
                id="flow-title"
                type="text"
                required
                placeholder="Enter flow title"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-slate-700"
                value={flowTitle}
                onChange={(e) => setFlowTitle(e.target.value)}
              />
            </div>

            <div
              id="flow-list"
              className="min-h-[200px] space-y-4 border p-2 rounded"
            >
              {flowItems.map((item, index) => (
                <div
                  key={`${item.viewId}-${index}`}
                  className="p-4 border rounded bg-white flex flex-col md:flex-row md:items-center md:justify-between"
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-800">
                      {item.title} (Version {item.versionIndex})
                    </span>
                  </div>

                  <div className="flex gap-2 mt-2 md:mt-0">
                    <button
                      onClick={() => handleMoveUp(index)}
                      className="text-slate-900 hover:text-slate-700"
                      title="Move Up"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      className="text-slate-900 hover:text-slate-700"
                      title="Move Down"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleRemove(index)}
                      className="text-red-900 hover:text-red-700"
                      title="Remove from Flow"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              id="save-flow"
              className="mt-4 w-full bg-slate-900 text-white py-2 rounded hover:bg-slate-700 transition-colors disabled:opacity-50"
              onClick={handleSaveFlow}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save Flow"}
            </button>
          </div>
        </div>

        {/* Desktop view - Side by side */}
        <div className="hidden md:flex md:flex-row md:gap-6">
          {/* Views Column */}
          <div
            id="views-section"
            className="w-1/2 border p-4 rounded shadow-sm"
          >
            <h2 className="text-xl font-semibold mb-4">Views</h2>
            <div id="views-list" className="space-y-4">
              {views.length === 0 ? (
                <p className="text-gray-600">No valid views found.</p>
              ) : (
                views.map((view) => (
                  <div
                    key={view._id}
                    className="view-card p-4 border rounded flex justify-between items-center bg-slate-100"
                    data-view-id={view._id}
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, view._id)}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{view.title}</span>
                      {/* Version dropdown */}
                      <div className="inline-block">
                        <select
                          className="version-select mt-2 text-sm text-slate-500"
                          value={view.selectedVersionId}
                          onChange={(e) => handleVersionChange(view._id, e.target.value)}
                        >
                          {view.versions
                            .slice()
                            .sort((a, b) => b.versionIndex - a.versionIndex) // sort descending: most recent first
                            .map((ver) => (
                              <option key={ver.versionId} value={ver.versionId}>
                                Version {ver.versionIndex}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    <button
                      className="move-to-flow text-slate-900 hover:text-slate-700"
                      title="Add to Flow"
                      onClick={() => handleAddToFlow(view)}
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Flow Column */}
          <div
            id="flow-section"
            className="w-1/2 border p-4 rounded shadow-sm"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <h2 className="text-xl font-semibold mb-4">Flow</h2>

            <div className="mb-4">
              <input
                id="flow-title"
                type="text"
                required
                placeholder="Enter flow title"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-slate-700"
                value={flowTitle}
                onChange={(e) => setFlowTitle(e.target.value)}
              />
            </div>

            <div
              id="flow-list"
              className="min-h-[200px] space-y-4 border p-2 rounded"
            >
              {flowItems.map((item, index) => (
                <div
                  key={`${item.viewId}-${index}`}
                  className="p-4 border rounded bg-white flex flex-col md:flex-row md:items-center md:justify-between"
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-800">
                      {item.title} (Version {item.versionIndex})
                    </span>
                  </div>

                  <div className="flex gap-2 mt-2 md:mt-0">
                    <button
                      onClick={() => handleMoveUp(index)}
                      className="text-slate-900 hover:text-slate-700"
                      title="Move Up"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      className="text-slate-900 hover:text-slate-700"
                      title="Move Down"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleRemove(index)}
                      className="text-red-900 hover:text-red-700"
                      title="Remove from Flow"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              id="save-flow"
              className="mt-4 w-full bg-slate-900 text-white py-2 rounded hover:bg-slate-700 transition-colors disabled:opacity-50"
              onClick={handleSaveFlow}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save Flow"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BuildFlow;